import { useState, useEffect, useCallback } from "react";
import { Wallet, Ban } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDateTime, todayIST } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Payments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState("all");
  const [method, setMethod] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [voidReason, setVoidReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (service !== "all") params.service = service;
      if (method !== "all") params.method = method;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get("/payments", { params });
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }, [service, method, from, to]);

  useEffect(() => { load(); }, [load]);

  const total = items.filter((i) => !i.voided).reduce((s, i) => s + i.amount, 0);

  const doVoid = async (id) => {
    if (!voidReason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/payments/${id}/void`, { reason: voidReason });
      toast.success("Payment voided");
      setVoidReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Payments" subtitle={`Actual collection ${inr(total)} · ${items.length} records`} testid="payments-page" />

      <Card className="p-3 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Select value={service} onValueChange={setService}>
            <SelectTrigger data-testid="pay-filter-service"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Services</SelectItem><SelectItem value="clinic">Clinic</SelectItem><SelectItem value="home">Home</SelectItem></SelectContent>
          </Select>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="pay-filter-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Methods</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank">Bank</SelectItem><SelectItem value="card">Card</SelectItem></SelectContent>
          </Select>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pay-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="pay-to" />
        </div>
      </Card>

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState icon={Wallet} title="No payments found" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b bg-slate-50/50">
              <th className="py-3 px-4">Payment ID</th><th>Patient</th><th>Date</th><th>Type</th><th>Method</th><th>Collected By</th><th className="text-right">Amount</th>{user?.role === "admin" && <th></th>}
            </tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.payment_id}`} className={`border-b border-slate-50 ${p.voided ? "opacity-50" : ""}`}>
                  <td className="py-2.5 px-4 font-medium text-slate-600">{p.payment_id}</td>
                  <td>{p.patient_name}</td>
                  <td className="text-slate-500">{fmtDateTime(p.date)}</td>
                  <td className="capitalize">{p.type}</td>
                  <td className="uppercase text-xs"><Badge variant="outline">{p.method}</Badge></td>
                  <td className="capitalize text-slate-500">{p.collected_by_role}</td>
                  <td className="text-right font-semibold tabular-nums">{p.voided ? <span className="line-through">{inr(p.amount)}</span> : inr(p.amount)}</td>
                  {user?.role === "admin" && (
                    <td className="pr-3">
                      {!p.voided ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="h-7 text-bk-danger" data-testid={`void-${p.payment_id}`}><Ban className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Void payment {p.payment_id}?</AlertDialogTitle><AlertDialogDescription>Financial records are never deleted. Voiding is traceable and reverses advance balance if applicable.</AlertDialogDescription></AlertDialogHeader>
                            <div className="space-y-1.5"><Label>Reason *</Label><Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g. Incorrect amount entered" data-testid="void-reason" /></div>
                            <AlertDialogFooter><AlertDialogCancel onClick={() => setVoidReason("")}>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => doVoid(p.id)} className="bg-bk-danger hover:bg-red-700">Void</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : <Badge variant="outline" className="text-bk-danger border-red-200">Voided</Badge>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
